'use client';

import Link from 'next/link';
import {
  ArrowRight,
  BookOpen,
  Camera,
  ChevronRight,
  Gift,
  Globe,
  HelpCircle,
  Images,
  MapPinned,
  Shirt,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';

type LinkItem = {
  icon: React.ReactNode;
  label: string;
  href: string;
  description?: string;
  external?: boolean;
};

function AppleIcon() {
  return (
    <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GooglePlayIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
      <path d="M3.4 1.6c-.3.2-.4.6-.4 1v18.8c0 .4.2.7.4.9l10.6-10.6L3.4 1.6z" fill="#00d4ff" />
      <path d="M17.7 8.6 4.6 1.2c-.4-.2-.7-.2-1 0l10.6 10.5 3.5-3.1z" fill="#00f176" />
      <path d="m17.7 15.4-3.5-3.1L3.6 22.8c.3.2.6.2 1 0l13.1-7.4z" fill="#ff3a44" />
      <path d="m21 11-3.3-1.9-3.7 2.9 3.7 2.9 3.3-1.9c1-.6 1-2 0-2z" fill="#ffce00" />
    </svg>
  );
}

const ICON_CLASS = 'h-5 w-5 text-[#c5b358]';

const QUICK_LINKS: LinkItem[] = [
  {
    icon: <UserPlus className={ICON_CLASS} aria-hidden />,
    label: 'Register Now',
    href: '/register',
    description: 'Sign up for your nearest ride',
  },
  {
    icon: <Globe className={ICON_CLASS} aria-hidden />,
    label: 'Live Ride Tracker',
    href: '/live',
    description: 'Follow rides around the world',
  },
  {
    icon: <AppleIcon />,
    label: 'Download for iOS',
    href: 'https://apps.apple.com/us/app/distinguished-gentlemans-ride/id6475795204',
    description: 'DGR App on the App Store',
    external: true,
  },
  {
    icon: <GooglePlayIcon />,
    label: 'Download for Android',
    href: 'https://play.google.com/store/apps/details?id=com.gentlemansride.dgr',
    description: 'DGR App on Google Play',
    external: true,
  },
  {
    icon: <BookOpen className={ICON_CLASS} aria-hidden />,
    label: 'Bike Style Guide',
    href: '/about/style-guide',
    description: 'Motorcycle eligibility',
  },
  {
    icon: <Shirt className={ICON_CLASS} aria-hidden />,
    label: 'Keeping it Dapper',
    href: '/about/dapper-guide',
    description: 'What to wear on ride day',
  },
  {
    icon: <Camera className={ICON_CLASS} aria-hidden />,
    label: 'Upload Photos & Videos',
    href: '/upload',
    description: 'Share your ride day content',
  },
  {
    icon: <Images className={ICON_CLASS} aria-hidden />,
    label: 'Gallery',
    href: '/gallery',
    description: 'Photos from past rides',
  },
  {
    icon: <MapPinned className={ICON_CLASS} aria-hidden />,
    label: 'Change Ride City',
    href: '/my/events',
    description: 'Manage events on your profile',
  },
  {
    icon: <Gift className={ICON_CLASS} aria-hidden />,
    label: 'Rewards & Orders',
    href: '/my/orders',
    description: 'Stickers, patches and pins',
  },
  {
    icon: <HelpCircle className={ICON_CLASS} aria-hidden />,
    label: 'FAQs',
    href: '/about/faq',
    description: 'Other questions answered',
  },
];

export function RideDayPage() {
  const { isAuthenticated, registrationStatus } = useAuth({ hydrateFromQuery: true });

  const registeredEventName =
    isAuthenticated && registrationStatus?.eventName ? registrationStatus.eventName : null;
  const primaryLabel = registeredEventName
    ? `View ${registeredEventName} Details`
    : 'View Full Ride Details';

  return (
    <div className="relative min-h-[80vh] overflow-hidden bg-stone-950">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-[#c5b358]/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-40 h-80 w-80 rounded-full bg-[#c5b358]/10 blur-3xl"
      />

      <div className="relative mx-auto w-full max-w-md px-5 pb-12 pt-28 sm:pb-16 sm:pt-36">
        <header className="text-center">
          <h1 className="font-display text-5xl uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">
            DGR <span className="text-dgr-gold">Quick Links</span>
          </h1>
        </header>

        <Link
          href="/my/dashboard"
          className="mt-7 flex items-center justify-between gap-3 rounded-xl border border-[#c5b358]/40 bg-[#c5b358] px-5 py-4 text-stone-950 transition hover:bg-[#b0a04e]"
        >
          <span className="flex flex-col items-start min-w-0">
            <span className="text-base font-semibold truncate max-w-full">{primaryLabel}</span>
            <span className="text-[11px] uppercase tracking-wider text-stone-900/70">
              {registeredEventName ? 'Your Ride' : 'My Dashboard'}
            </span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0" />
        </Link>

        <ul className="mt-6 space-y-2">
          {QUICK_LINKS.map((item) => {
            const content = (
              <>
                <span
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-stone-800 bg-stone-950"
                >
                  {item.icon}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-white">{item.label}</span>
                  {item.description ? (
                    <span className="block truncate text-[11px] text-stone-400">
                      {item.description}
                    </span>
                  ) : null}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-stone-600 transition group-hover:text-[#c5b358]" />
              </>
            );

            return (
              <li key={item.href}>
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 rounded-xl border border-stone-800 bg-stone-900/60 px-4 py-3 transition hover:border-[#c5b358]/40 hover:bg-stone-900"
                  >
                    {content}
                  </a>
                ) : (
                  <Link
                    href={item.href}
                    className="group flex items-center gap-4 rounded-xl border border-stone-800 bg-stone-900/60 px-4 py-3 transition hover:border-[#c5b358]/40 hover:bg-stone-900"
                  >
                    {content}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8 space-y-3 rounded-xl border border-stone-800/60 bg-stone-900/30 p-4 text-[12px] leading-relaxed text-stone-400">
          <p>
            <strong className="text-stone-200">Missing stickers, patches or pins?</strong> Rewards
            are dispatched weekly via regular post. Check{' '}
            <Link href="/my/orders" className="text-[#c5b358] underline-offset-2 hover:underline">
              My Orders
            </Link>{' '}
            for tracking updates. Please allow up to 4 weeks for delivery.
          </p>
          <p>
            <strong className="text-stone-200">Missing donation?</strong> If you accidentally
            donated as a general donation, we can manually assign it to your profile after the ride
            weekend.
          </p>
        </div>
      </div>
    </div>
  );
}
